import React, { useEffect, useState } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { financeiroAPI } from '../../lib/api';
import { ChevronDown, ChevronRight, CheckCircle2, Circle, Printer, Pencil, Trash2, CheckSquare, XSquare, RotateCcw, ArrowRightLeft, Beef, TrendingDown, Plus, Eye } from 'lucide-react';
import { toast } from 'sonner';
import ManejoGrid, { totalManejo } from './ManejoGrid';
import ManejoTransferModal from './ManejoTransferModal';
import ExpenseFormModal from './ExpenseFormModal';
import ReciboNomeModal from './ReciboNomeModal';
import ClientQuickViewModal from '../../components/ClientQuickViewModal';

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDate = (d) => d ? new Date(d + (d.length === 10 ? 'T12:00:00' : '')).toLocaleDateString('pt-BR') : '-';

const STATUS_LABELS = {
  em_andamento: { label: 'Em Andamento', color: 'bg-emerald-500/10 text-emerald-700 border-emerald-300' },
  ativo: { label: 'Em Andamento', color: 'bg-emerald-500/10 text-emerald-700 border-emerald-300' }, // legado
  pausado: { label: 'Pausado', color: 'bg-amber-500/10 text-amber-700 border-amber-300' },
  aguardando_cliente: { label: 'Aguardando Cliente', color: 'bg-purple-500/10 text-purple-700 border-purple-300' },
  aguardando_pagamento: { label: 'Aguardando Pagamento', color: 'bg-blue-500/10 text-blue-700 border-blue-300' },
  concluido: { label: 'Concluído', color: 'bg-sky-500/10 text-sky-700 border-sky-300' },
  cancelado: { label: 'Cancelado', color: 'bg-rose-500/10 text-rose-700 border-rose-300' },
};

export default function ServiceListRow({ svc: svcProp, onChanged, onEdit, onDelete, onPrintRecibo, finalizados = false, defaultExpanded = false }) {
  const [showCliente, setShowCliente] = useState(false);
  // svcLocal: cópia local do serviço para permitir atualizações sem reload do parent
  // (evita que o expand colapse ao marcar parcela como paga)
  const [svcLocal, setSvcLocal] = useState(svcProp);
  useEffect(() => { setSvcLocal(svcProp); }, [svcProp]);
  // Alias para manter referências `svc.X` em todo o JSX/handlers
  const svc = svcLocal;

  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showTransfer, setShowTransfer] = useState(false);
  const [despesas, setDespesas] = useState([]);
  const [loadingDespesas, setLoadingDespesas] = useState(false);
  const [showDespesaForm, setShowDespesaForm] = useState(false);
  const [editingDespesa, setEditingDespesa] = useState(null);
  const [reciboModal, setReciboModal] = useState(null); // { svc, parcela } | null
  const status = STATUS_LABELS[svc.status] || STATUS_LABELS.em_andamento;
  const manejoTotal = svc.gerir_manejo ? (svc.manejo_total ?? totalManejo(svc.manejo)) : null;

  const loadDespesas = async () => {
    setLoadingDespesas(true);
    try {
      const r = await financeiroAPI.listExpenses({ servico_id: svc.id });
      setDespesas(r.data || []);
    } catch (err) {
      // silencioso (não bloquear visualização do serviço)
    } finally {
      setLoadingDespesas(false);
    }
  };

  useEffect(() => {
    if (expanded) loadDespesas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, svc.id]);

  const handleNovaDespesa = (e) => {
    e.stopPropagation();
    setEditingDespesa(null);
    setShowDespesaForm(true);
  };

  const handleEditDespesa = (e, desp) => {
    e.stopPropagation();
    setEditingDespesa(desp);
    setShowDespesaForm(true);
  };

  const handleDeleteDespesa = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Excluir esta despesa?')) return;
    try {
      await financeiroAPI.deleteExpense(id);
      toast.success('Despesa excluída');
      loadDespesas();
    } catch (err) {
      toast.error('Erro ao excluir despesa');
    }
  };

  const handleToggleParcela = async (e, parcelaId) => {
    e.stopPropagation();
    try {
      await financeiroAPI.toggleParcelaPago(svc.id, parcelaId);
      // Re-fetch SOMENTE este serviço (mantém o expand aberto, não dispara parent reload)
      const r = await financeiroAPI.getService(svc.id);
      if (r.data) setSvcLocal(r.data);
    } catch (err) {
      toast.error('Erro ao atualizar parcela');
    }
  };

  // Handlers do recibo: capturam nome de quem recebe via modal
  const openReciboModal = (e, parcela = null) => {
    if (e) e.stopPropagation();
    setReciboModal({ svc, parcela });
  };
  const closeReciboModal = () => setReciboModal(null);
  const confirmRecibo = (nomeRecebedor) => {
    const ctx = reciboModal;
    setReciboModal(null);
    if (!ctx) return;
    onPrintRecibo && onPrintRecibo(ctx.svc, ctx.parcela, nomeRecebedor);
  };

  const handleFinalizar = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Finalizar o serviço "${svc.titulo}"?`)) return;
    try {
      await financeiroAPI.finalizar(svc.id, {});
      toast.success('Serviço finalizado');
      onChanged && onChanged();
    } catch (err) {
      toast.error('Erro ao finalizar');
    }
  };

  const handleCancelar = async (e) => {
    e.stopPropagation();
    const motivo = window.prompt(`Cancelar o serviço "${svc.titulo}".\nMotivo (opcional):`);
    if (motivo === null) return; // cancelou o prompt
    const cancelarParcelas = window.confirm('Deseja cancelar TAMBÉM as parcelas pendentes?\n\n• OK = parcelas pendentes serão canceladas (não aparecem em "a receber")\n• Cancelar = manter as parcelas como estão (registro histórico)');
    try {
      await financeiroAPI.cancelar(svc.id, { motivo: motivo || '', cancelar_parcelas_pendentes: cancelarParcelas });
      toast.success('Serviço cancelado');
      onChanged && onChanged();
    } catch (err) {
      toast.error('Erro ao cancelar');
    }
  };

  const handleReabrir = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Reabrir o serviço "${svc.titulo}"? Ele voltará para "Em Andamento".`)) return;
    try {
      await financeiroAPI.reabrir(svc.id);
      toast.success('Serviço reaberto');
      onChanged && onChanged();
    } catch (err) {
      toast.error('Erro ao reabrir');
    }
  };

  const parcelasAtivas = (svc.parcelas || []).filter(p => !p.cancelada);
  const valorPago = svc.valor_pago || 0;
  const valorPendente = svc.valor_pendente || 0;

  return (
    <div className="border rounded-lg bg-card overflow-hidden transition-all" data-testid={`servico-row-${svc.id}`}>
      {/* ===== Linha compacta (clicável) — Ordem: Cliente > Serviço > Responsável > Valor > Início > Manejo ===== */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full grid grid-cols-12 gap-2 items-center px-4 py-3 hover:bg-muted/40 transition-colors text-left"
        data-testid={`row-toggle-${svc.id}`}
      >
        <div className="col-span-1 flex items-center justify-center">
          {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
        </div>
        <div className="col-span-3 min-w-0">
          <p className="font-semibold text-sm truncate">{svc.cliente_nome || '—'}</p>
          {svc.cliente_cpf && <p className="text-[11px] text-muted-foreground">{svc.cliente_cpf}</p>}
        </div>
        <div className="col-span-3 min-w-0">
          <p className="text-sm truncate">{svc.titulo}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <Badge className={status.color + ' border text-[10px] py-0 px-1.5'}>{status.label}</Badge>
            {svc.tipo_servico_nome && <span className="text-[11px] text-muted-foreground truncate">· {svc.tipo_servico_nome}</span>}
          </div>
        </div>
        <div className="col-span-2 min-w-0">
          <p className="text-sm truncate" data-testid={`responsavel-${svc.id}`}>
            {svc.criado_por_nome || <span className="text-muted-foreground italic">—</span>}
          </p>
        </div>
        <div className="col-span-1 text-right">
          <p className="font-semibold text-sm">{fmt(svc.valor_total)}</p>
          {valorPendente > 0 && <p className="text-[10px] text-amber-600">Pend: {fmt(valorPendente)}</p>}
        </div>
        <div className="col-span-1 text-xs text-muted-foreground">{fmtDate(svc.data_inicio)}</div>
        <div className="col-span-1 text-center" data-testid={`manejo-col-${svc.id}`}>
          {manejoTotal !== null ? (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-bold text-sm">
              <Beef className="w-3 h-3" />
              {manejoTotal}
            </div>
          ) : (
            <span className="text-[10px] text-muted-foreground">—</span>
          )}
        </div>
      </button>

      {/* ===== Detalhes expandidos ===== */}
      {expanded && (
        <div className="border-t bg-muted/20 p-4 space-y-4" data-testid={`row-details-${svc.id}`}>
          {svc.descricao && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Descrição</p>
              <p className="text-sm">{svc.descricao}</p>
            </div>
          )}

          {/* Itens / subserviços */}
          {(svc.itens || []).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Detalhamento ({(svc.itens || []).length} subserviço{(svc.itens || []).length > 1 ? 's' : ''})</p>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="bg-emerald-50 border-b">
                      <td className="p-2 font-medium">Serviço Principal</td>
                      <td className="p-2 text-right font-medium w-32">{fmt(svc.valor_servico_principal || 0)}</td>
                    </tr>
                    {svc.itens.map((it, i) => (
                      <tr key={it.id || i} className="border-b last:border-b-0 bg-card">
                        <td className="p-2 pl-6 text-muted-foreground">▸ {it.descricao || <span className="italic">sem descrição</span>}</td>
                        <td className="p-2 text-right">{fmt(it.valor)}</td>
                      </tr>
                    ))}
                    <tr className="bg-emerald-100 font-bold border-t-2 border-emerald-300">
                      <td className="p-2">TOTAL</td>
                      <td className="p-2 text-right">{fmt(svc.valor_total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Metadados */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-[11px] text-muted-foreground uppercase">Categoria</p>
              <p>{svc.categoria_nome ? (
                <span className="inline-block px-2 py-0.5 rounded text-xs" style={{ background: `${svc.categoria_cor}22`, color: svc.categoria_cor }}>{svc.categoria_nome}</span>
              ) : '—'}</p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground uppercase">Forma de Pagamento</p>
              <p>{svc.forma_pagamento_nome || '—'}</p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground uppercase">Conclusão</p>
              <p>{fmtDate(svc.data_conclusao)}</p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground uppercase">Pago / Pendente</p>
              <p><span className="text-emerald-600">{fmt(valorPago)}</span> / <span className="text-amber-600">{fmt(valorPendente)}</span></p>
            </div>
          </div>

          {/* Motivo cancelamento */}
          {svc.status === 'cancelado' && svc.motivo_cancelamento && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg">
              <p className="text-xs font-semibold text-rose-700 uppercase mb-1">Motivo do Cancelamento</p>
              <p className="text-sm text-rose-900">{svc.motivo_cancelamento}</p>
            </div>
          )}

          {/* Parcelas */}
          {parcelasAtivas.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Parcelas ({parcelasAtivas.length})</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {(svc.parcelas || []).map(p => (
                  <div
                    key={p.id}
                    className={`p-2.5 border rounded-lg flex items-center gap-2 text-sm ${p.cancelada ? 'bg-rose-50 border-rose-200 opacity-60 line-through' : p.pago ? 'bg-emerald-50 border-emerald-200' : 'bg-card'}`}
                  >
                    <button
                      onClick={(e) => handleToggleParcela(e, p.id)}
                      disabled={p.cancelada || svc.status === 'cancelado'}
                      data-testid={`parcela-toggle-${p.id}`}
                      className="shrink-0 disabled:opacity-40"
                    >
                      {p.pago ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Circle className="w-5 h-5 text-muted-foreground" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">#{p.numero} · {fmt(p.valor)}</p>
                      <p className="text-[11px] text-muted-foreground">Venc: {fmtDate(p.vencimento)}{p.cancelada ? ' · cancelada' : ''}</p>
                    </div>
                    {p.pago && !p.cancelada && (
                      <button onClick={(e) => openReciboModal(e, p)} title="Recibo" data-testid={`recibo-${p.id}`}>
                        <Printer className="w-4 h-4 text-sky-600 hover:text-sky-800" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Manejo (se gerir_manejo) */}
          {svc.gerir_manejo && (
            <div data-testid={`manejo-detalhe-${svc.id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase flex items-center gap-1.5">
                  <Beef className="w-3.5 h-3.5 text-amber-600" />
                  Manejo · {manejoTotal} unidade{manejoTotal !== 1 ? 's' : ''}
                </p>
                {!finalizados && svc.status !== 'concluido' && svc.status !== 'cancelado' && manejoTotal > 0 && (
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setShowTransfer(true); }} data-testid={`btn-transferir-${svc.id}`}>
                    <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5 text-cyan-600" /> Transferir
                  </Button>
                )}
              </div>
              <ManejoGrid value={svc.manejo} onChange={() => {}} disabled label="Atual" />
            </div>
          )}

          {/* Despesas vinculadas */}
          <div data-testid={`despesas-vinculadas-${svc.id}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase flex items-center gap-1.5">
                <TrendingDown className="w-3.5 h-3.5 text-rose-600" />
                Despesas vinculadas
                {despesas.length > 0 && (
                  <span className="ml-1 text-rose-600 font-bold normal-case">
                    · {despesas.length} · {fmt(despesas.reduce((s, d) => s + (d.valor || 0), 0))}
                  </span>
                )}
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={handleNovaDespesa}
                data-testid={`btn-nova-despesa-${svc.id}`}
              >
                <Plus className="w-3.5 h-3.5 mr-1.5 text-rose-600" /> Despesa
              </Button>
            </div>
            {loadingDespesas ? (
              <p className="text-xs text-muted-foreground">Carregando...</p>
            ) : despesas.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">
                Nenhuma despesa vinculada a este serviço.
              </p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    {despesas.map(d => (
                      <tr key={d.id} className="border-b last:border-b-0 bg-card hover:bg-muted/30">
                        <td className="p-2 w-24 text-xs text-muted-foreground">{fmtDate(d.data)}</td>
                        <td className="p-2">
                          {d.categoria_nome ? (
                            <span
                              className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: `${d.categoria_cor}22`, color: d.categoria_cor }}
                            >
                              {d.categoria_nome}
                            </span>
                          ) : (
                            <span className="text-xs italic text-muted-foreground">sem categoria</span>
                          )}
                        </td>
                        <td className="p-2 text-xs text-muted-foreground truncate max-w-[240px]">
                          {d.observacoes || '—'}
                        </td>
                        <td className="p-2 text-right font-semibold text-rose-600 w-28">{fmt(d.valor)}</td>
                        <td className="p-2 text-center whitespace-nowrap w-24">
                          <Button size="sm" variant="ghost" onClick={(e) => handleEditDespesa(e, d)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={(e) => handleDeleteDespesa(e, d.id)}>
                            <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Observações */}
          {svc.observacoes && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Observações</p>
              <p className="text-sm whitespace-pre-line">{svc.observacoes}</p>
            </div>
          )}

          {/* Ações */}
          <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
            {svc.cliente_id && (
              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setShowCliente(true); }} data-testid={`btn-ver-cliente-${svc.id}`}>
                <Eye className="w-3.5 h-3.5 mr-1.5" /> Ver Cliente
              </Button>
            )}
            {!finalizados && svc.status !== 'concluido' && svc.status !== 'cancelado' && (
              <>
                <Button size="sm" variant="outline" onClick={handleFinalizar} data-testid={`btn-finalizar-${svc.id}`}>
                  <CheckSquare className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> Finalizar
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelar} data-testid={`btn-cancelar-${svc.id}`}>
                  <XSquare className="w-3.5 h-3.5 mr-1.5 text-rose-600" /> Cancelar
                </Button>
              </>
            )}
            {finalizados && (
              <Button size="sm" variant="outline" onClick={handleReabrir} data-testid={`btn-reabrir-${svc.id}`}>
                <RotateCcw className="w-3.5 h-3.5 mr-1.5 text-cyan-600" /> Reabrir
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onEdit(svc); }} data-testid={`edit-${svc.id}`}>
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Editar
            </Button>
            <Button size="sm" variant="outline" onClick={(e) => openReciboModal(e, null)} data-testid={`recibo-total-${svc.id}`}>
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Recibo Total
            </Button>
            <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); onDelete(svc.id); }} data-testid={`del-${svc.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Cadastro do cliente em popup (igual ao "Ver Cliente" do projeto) */}
      {showCliente && (
        <ClientQuickViewModal clientId={svc.cliente_id} onClose={() => setShowCliente(false)} />
      )}

      {showTransfer && (
        <ManejoTransferModal
          origem={svc}
          onClose={() => setShowTransfer(false)}
          onTransferido={() => { setShowTransfer(false); onChanged && onChanged(); }}
        />
      )}

      {showDespesaForm && (
        <ExpenseFormModal
          editing={editingDespesa}
          defaultServicoId={svc.id}
          lockServico={true}
          onClose={() => setShowDespesaForm(false)}
          onSaved={() => {
            setShowDespesaForm(false);
            loadDespesas();
          }}
        />
      )}

      <ReciboNomeModal
        open={!!reciboModal}
        defaultNome=""
        onCancel={closeReciboModal}
        onConfirm={confirmRecibo}
      />
    </div>
  );
}
